import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import gsap from 'gsap'
import { useCameraStore } from '../stores/useCameraStore'

const DOOR_OPEN_DURATION = 0.5
const DOOR_CLOSE_DURATION = 0.5
const AUTO_CLOSE_DELAY = 30_000 // 30 seconds
const DOOR_EASE = 'power2.inOut'

/** POI ids that represent battery clusters. */
const BATTERY_POI_IDS = new Set(['battery-cluster-1', 'battery-cluster-2', 'battery-cluster-3'])

/**
 * Automatically opens/closes battery cluster doors based on the active POI.
 *
 * - Battery POI selected → open corresponding cluster doors (morph targets)
 * - After 30 s → auto-close
 * - Different battery POI selected → snap-close old doors, open new ones
 * - Non-battery POI or deselection → close any open doors
 */
export function useDoorInteraction(scene: THREE.Object3D): void {
  // Container name → morph-target meshes discovered from the scene
  const containerMeshesRef = useRef<Map<string, THREE.Mesh[]>>(new Map())
  // Dynamic mapping: battery POI id → container names (built at discovery time)
  const poiToContainersRef = useRef<Record<string, string[]>>({})
  // Currently open POI id (null = all closed)
  const openPOIRef = useRef<string | null>(null)
  // Active GSAP tweens (killed on cleanup / immediate close)
  const activeTweensRef = useRef<gsap.core.Tween[]>([])
  // Auto-close timer handle
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const currentPOI = useCameraStore((s) => s.currentPOI)
  const pois = useCameraStore((s) => s.pois)

  // ── Discovery: find ClusterContainer groups, then map them to battery POIs ──
  useEffect(() => {
    const containerMeshes = new Map<string, THREE.Mesh[]>()
    const containerPositions = new Map<string, THREE.Vector3>()
    const worldPos = new THREE.Vector3()

    scene.traverse((child) => {
      if (child.name.startsWith('ClusterContainer') && child.children.length > 0) {
        const meshes: THREE.Mesh[] = []
        child.children.forEach((c) => {
          if (!(c instanceof THREE.Mesh)) return
          if (!c.morphTargetInfluences || c.morphTargetInfluences.length === 0) return
          meshes.push(c)
        })
        if (meshes.length > 0) {
          containerMeshes.set(child.name, meshes)
          child.getWorldPosition(worldPos)
          containerPositions.set(child.name, worldPos.clone())
        }
      }
    })

    containerMeshesRef.current = containerMeshes

    // Build dynamic POI → container mapping using spatial proximity
    const batteryPOIs = pois.filter((p) => BATTERY_POI_IDS.has(p.id))
    const mapping: Record<string, string[]> = {}
    const assigned = new Set<string>()

    // For each battery POI, find the closest unassigned containers
    // Sort battery POIs by id for deterministic ordering
    const sortedBatteryPOIs = [...batteryPOIs].sort((a, b) => a.id.localeCompare(b.id))
    const containersPerPOI = batteryPOIs.length > 0
      ? Math.ceil(containerMeshes.size / batteryPOIs.length)
      : 0

    for (const poi of sortedBatteryPOIs) {
      const poiTarget = new THREE.Vector3(poi.target.x, poi.target.y, poi.target.z)

      // Rank unassigned containers by distance to this POI's target
      const ranked = [...containerPositions.entries()]
        .filter(([name]) => !assigned.has(name))
        .sort(([, posA], [, posB]) => posA.distanceTo(poiTarget) - posB.distanceTo(poiTarget))

      const closest = ranked.slice(0, containersPerPOI).map(([name]) => name)
      closest.forEach((n) => assigned.add(n))
      mapping[poi.id] = closest
    }

    poiToContainersRef.current = mapping
    console.log(
      `Door interaction: found ${containerMeshes.size} containers:`,
      [...containerMeshes.keys()],
      '\nPOI→container mapping:',
      mapping
    )
  }, [scene, pois])

  // ── React to currentPOI changes ──
  useEffect(() => {
    const poiId = currentPOI?.id ?? null
    console.log('[DoorInteraction] POI changed:', poiId, '| containers loaded:', containerMeshesRef.current.size)

    const clearTimer = () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
    }

    const killTweens = () => {
      activeTweensRef.current.forEach((t) => t.kill())
      activeTweensRef.current = []
    }

    /** Animate morph targets for all containers belonging to a POI. */
    const animateContainers = (
      batteryPOIId: string,
      target: number,
      duration: number,
      onComplete?: () => void
    ) => {
      const containerNames = poiToContainersRef.current[batteryPOIId]
      if (!containerNames || containerNames.length === 0) {
        onComplete?.()
        return
      }

      let remaining = 0
      containerNames.forEach((name) => {
        const meshes = containerMeshesRef.current.get(name)
        console.log(`[DoorInteraction] Container "${name}": ${meshes ? meshes.length : 0} meshes found`)
        if (!meshes || meshes.length === 0) return

        const current = meshes[0].morphTargetInfluences![0]
        if (Math.abs(current - target) < 0.001) return

        remaining++
        const proxy = { influence: current }
        const tween = gsap.to(proxy, {
          influence: target,
          duration,
          ease: DOOR_EASE,
          onUpdate: () => {
            meshes.forEach((m) => {
              m.morphTargetInfluences![0] = proxy.influence
            })
          },
          onComplete: () => {
            const idx = activeTweensRef.current.indexOf(tween)
            if (idx !== -1) activeTweensRef.current.splice(idx, 1)
            remaining--
            if (remaining === 0) onComplete?.()
          },
        })
        activeTweensRef.current.push(tween)
      })

      // If nothing needed animating, fire immediately
      if (remaining === 0) onComplete?.()
    }

    /** Snap-close all containers for a POI (no animation). */
    const snapClosed = (batteryPOIId: string) => {
      const containerNames = poiToContainersRef.current[batteryPOIId]
      if (!containerNames || containerNames.length === 0) return
      containerNames.forEach((name) => {
        const meshes = containerMeshesRef.current.get(name)
        if (!meshes) return
        meshes.forEach((m) => {
          m.morphTargetInfluences![0] = 0
        })
      })
    }

    // ── Main logic ──
    clearTimer()

    const isBattery = poiId !== null && BATTERY_POI_IDS.has(poiId)

    if (isBattery) {
      const previousPOI = openPOIRef.current

      if (previousPOI === poiId) {
        // Same battery POI re-selected → just reset the auto-close timer
        autoCloseTimerRef.current = setTimeout(() => {
          if (openPOIRef.current === poiId) {
            openPOIRef.current = null
            animateContainers(poiId, 0, DOOR_CLOSE_DURATION)
          }
        }, AUTO_CLOSE_DELAY)
        return
      }

      // Close previous cluster immediately
      if (previousPOI) {
        killTweens()
        snapClosed(previousPOI)
      }

      // Open the new cluster
      openPOIRef.current = poiId
      animateContainers(poiId, 1, DOOR_OPEN_DURATION, () => {
        autoCloseTimerRef.current = setTimeout(() => {
          if (openPOIRef.current === poiId) {
            openPOIRef.current = null
            animateContainers(poiId, 0, DOOR_CLOSE_DURATION)
          }
        }, AUTO_CLOSE_DELAY)
      })
    } else {
      // Non-battery POI or null → close any open doors
      const previousPOI = openPOIRef.current
      if (previousPOI) {
        killTweens()
        openPOIRef.current = null
        if (poiId !== null) {
          // Switching to a non-battery POI → snap close
          snapClosed(previousPOI)
        } else {
          // Deselecting → animate close
          animateContainers(previousPOI, 0, DOOR_CLOSE_DURATION)
        }
      }
    }

    return () => {
      clearTimer()
      killTweens()
    }
  }, [currentPOI])

  // Unmount cleanup: reset all morph targets
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current !== null) {
        clearTimeout(autoCloseTimerRef.current)
      }
      activeTweensRef.current.forEach((t) => t.kill())
      containerMeshesRef.current.forEach((meshes) => {
        meshes.forEach((m) => {
          if (m.morphTargetInfluences && m.morphTargetInfluences.length > 0) {
            m.morphTargetInfluences[0] = 0
          }
        })
      })
    }
  }, [])
}
